import { captureError } from '@smg-real-estate/browser-rum';
import type { VueI18n } from 'vue-i18n';
import type { ActionContext } from 'vuex';

import * as ToplistingApiService from '@/api/toplistingService';
import { getRegionalHighlightStatusForListing } from '@/helpers/regionalHighlightListingStatusLookup';
import { ToastTheme } from '@/store/app/AppStore';
import { ApiCallStatus } from '@/store/insertions/InsertionsStore';
import { runBackoffPoll } from '@/store/polling/runBackoffPoll';
import {
  regionalHighlightPurchaseActions,
  runRegionalHighlightOperation,
} from '@/store/toplisting/regionalHighlightPurchaseActions';
import { resolveInsertionOwnerId } from '@/store/toplisting/resolveInsertionOwnerId';
import type { InsertionFunnelState } from '@/store/types/insertion-funnel-store';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { InsertionStatus, type Insertion } from '@/types';
import type {
  RegionalHighlightAssignmentRequest,
  RegionalHighlightAssignmentResponse,
  RegionalHighlightCallbackRequest,
  RegionalHighlightCallbackResponse,
  RegionalHighlightDeactivationRequest,
  RegionalHighlightDraftAddress,
  RegionalHighlightListingStatus,
  RegionalHighlightListingStatusesByListingId,
  RegionalHighlightPurchaseOptionsResponse,
  RegionalHighlightPurchaseOrder,
  RegionalHighlightPurchaseOrdersResponse,
  RegionalHighlightSlot,
  RegionalHighlightSlotsResponse,
} from '@/types/regionalHighlight';

export type RegionalHighlightOperation =
  | 'slots'
  | 'listingStatuses'
  | 'assign'
  | 'deactivate'
  | 'callback'
  | 'purchaseOptions'
  | 'purchaseOrders'
  | 'createPurchaseOrder'
  | 'extendSlot'
  | 'autoRenew';

export interface RegionalHighlightApiStatus {
  slots: ApiCallStatus;
  listingStatuses: ApiCallStatus;
  assign: ApiCallStatus;
  deactivate: ApiCallStatus;
  callback: ApiCallStatus;
  purchaseOptions: ApiCallStatus;
  purchaseOrders: ApiCallStatus;
  createPurchaseOrder: ApiCallStatus;
  extendSlot: ApiCallStatus;
  autoRenew: ApiCallStatus;
}

export type RegionalHighlightErrors = Partial<Record<RegionalHighlightOperation, unknown>>;

/**
 * Poll cadence for a deferred Regional Highlight activation, ~58s in total.
 *
 * Deliberately a standalone budget rather than one derived from the insertion
 * processing poll: a Regional Highlight booking additionally waits for the importer
 * `LISTING_PUBLISHED` event to complete the pending assignment after the insertion
 * itself reaches `Published`. Provisional, to be tuned from telemetry.
 */
export const REGIONAL_HIGHLIGHT_POLL_DELAYS_MS = [
  2_000, 3_000, 5_000, 8_000, 10_000, 10_000, 10_000, 10_000,
];

export interface RegionalHighlightStateFields {
  regionalHighlightSlots?: RegionalHighlightSlot[];
  regionalHighlightSlotsResponse?: RegionalHighlightSlotsResponse;
  regionalHighlightListingStatuses?: RegionalHighlightListingStatusesByListingId;
  regionalHighlightPurchaseOptions?: RegionalHighlightPurchaseOptionsResponse;
  regionalHighlightPurchaseOrders?: RegionalHighlightPurchaseOrdersResponse;
  regionalHighlightLastPurchaseOrder?: RegionalHighlightPurchaseOrder;
  regionalHighlightApiStatus: RegionalHighlightApiStatus;
  regionalHighlightErrors: RegionalHighlightErrors;
  regionalHighlightLastAssignment?: RegionalHighlightAssignmentResponse;
  regionalHighlightLastCallback?: RegionalHighlightCallbackResponse;
  insertionIdsWithPendingRegionalHighlight: string[];
}

const getInitialRegionalHighlightApiStatus = (): RegionalHighlightApiStatus => ({
  slots: ApiCallStatus.READY,
  listingStatuses: ApiCallStatus.READY,
  assign: ApiCallStatus.READY,
  deactivate: ApiCallStatus.READY,
  callback: ApiCallStatus.READY,
  purchaseOptions: ApiCallStatus.READY,
  purchaseOrders: ApiCallStatus.READY,
  createPurchaseOrder: ApiCallStatus.READY,
  extendSlot: ApiCallStatus.READY,
  autoRenew: ApiCallStatus.READY,
});

export const getInitialRegionalHighlightStateFields = (): RegionalHighlightStateFields => ({
  regionalHighlightSlots: undefined,
  regionalHighlightSlotsResponse: undefined,
  regionalHighlightListingStatuses: undefined,
  regionalHighlightPurchaseOptions: undefined,
  regionalHighlightPurchaseOrders: undefined,
  regionalHighlightLastPurchaseOrder: undefined,
  regionalHighlightApiStatus: getInitialRegionalHighlightApiStatus(),
  regionalHighlightErrors: {},
  regionalHighlightLastAssignment: undefined,
  regionalHighlightLastCallback: undefined,
  insertionIdsWithPendingRegionalHighlight: [],
});

const refreshRegionalHighlightListingState = async (
  context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
  activeAgencyId: string,
  listingId: string,
): Promise<void> => {
  const listingIds = Array.from(
    new Set([...Object.keys(context.state.regionalHighlightListingStatuses ?? {}), listingId]),
  );

  await runRegionalHighlightOperation(context, 'slots', activeAgencyId, async () => {
    const response = await ToplistingApiService.fetchRegionalHighlightSlots(
      activeAgencyId,
      listingId,
    );
    return () => context.commit('setRegionalHighlightSlotsResponse', response);
  });
  await runRegionalHighlightOperation(context, 'listingStatuses', activeAgencyId, async () => {
    const statuses = await ToplistingApiService.fetchRegionalHighlightListingStatuses(
      activeAgencyId,
      { listingIds },
    );
    return () => context.commit('setRegionalHighlightListingStatuses', statuses);
  });
};

/**
 * One poll attempt for a deferred activation: the insertion is refetched first,
 * because a wizard draft only gets its listing id once it reaches `Published`, and
 * only then can the listing-keyed Regional Highlight status be read.
 *
 * The status request is deliberately not wrapped in `runRegionalHighlightOperation`:
 * this poll is detached from the page that owns `regionalHighlightApiStatus`, and it
 * must not flip that shared state on every attempt.
 *
 * A failing attempt is reported and treated as "not landed yet", so a single flaky
 * request does not end the poll early.
 */
const hasRegionalHighlightLanded = async (
  context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
  payload: { activeAgencyId: string; userId: string; insertionId: string },
): Promise<boolean> => {
  try {
    const insertion: Insertion | undefined = await context.dispatch('refetchInsertionById', {
      userId: payload.userId,
      insertionId: payload.insertionId,
    });

    const listingId = insertion?.listing?.id;
    if (insertion?.status !== InsertionStatus.Published || !listingId) {
      return false;
    }

    const statuses = await ToplistingApiService.fetchRegionalHighlightListingStatuses(
      payload.activeAgencyId,
      { listingIds: [listingId] },
    );
    context.commit('mergeRegionalHighlightListingStatuses', statuses);

    return getRegionalHighlightStatusForListing(statuses, listingId)?.hasRegionalHighlight === true;
  } catch (error) {
    captureError(error);
    return false;
  }
};

/**
 * Nudges the user to refresh once the activation budget is spent.
 *
 * The i18n bootstrap is imported lazily: a static import pulls the app-wide message
 * bundle into the module graph of every store consumer and breaks the specs that
 * partially mock `vue-i18n` without `createI18n`.
 */
const showRegionalHighlightPendingTimeoutToast = async (
  context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
): Promise<void> => {
  const { i18n } = await import('@/i18n-setup-hg');
  const translate = (key: string): string => (i18n.global as VueI18n).t(key).toString();

  await context.dispatch('showToast', {
    title: translate('manageAdsPage.toasts.regionalHighlightPendingTimeout.title'),
    message: translate('manageAdsPage.toasts.regionalHighlightPendingTimeout.message'),
    theme: ToastTheme.Warning,
  });
};

export const regionalHighlightGetters = {
  getRegionalHighlightSlots: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightSlot[] | undefined => state.regionalHighlightSlots,
  getRegionalHighlightSlotsResponse: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightSlotsResponse | undefined => state.regionalHighlightSlotsResponse,
  getRegionalHighlightListingStatuses: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightListingStatusesByListingId | undefined =>
    state.regionalHighlightListingStatuses,
  getRegionalHighlightListingStatusByListingId:
    (state: RegionalHighlightStateFields) =>
    (listingId: string): RegionalHighlightListingStatus | undefined =>
      getRegionalHighlightStatusForListing(state.regionalHighlightListingStatuses, listingId),
  getRegionalHighlightApiStatus: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightApiStatus => state.regionalHighlightApiStatus,
  getRegionalHighlightErrors: (state: RegionalHighlightStateFields): RegionalHighlightErrors =>
    state.regionalHighlightErrors,
  getRegionalHighlightLastAssignment: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightAssignmentResponse | undefined => state.regionalHighlightLastAssignment,
  getRegionalHighlightLastCallback: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightCallbackResponse | undefined => state.regionalHighlightLastCallback,
  getRegionalHighlightPurchaseOptions: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightPurchaseOptionsResponse | undefined => state.regionalHighlightPurchaseOptions,
  getRegionalHighlightPurchaseOrders: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightPurchaseOrdersResponse | undefined => state.regionalHighlightPurchaseOrders,
  getRegionalHighlightLastPurchaseOrder: (
    state: RegionalHighlightStateFields,
  ): RegionalHighlightPurchaseOrder | undefined => state.regionalHighlightLastPurchaseOrder,
  getInsertionIdsWithPendingRegionalHighlight: (state: RegionalHighlightStateFields): string[] =>
    state.insertionIdsWithPendingRegionalHighlight,
  getIsPendingRegionalHighlightByInsertionId:
    (state: RegionalHighlightStateFields) =>
    (insertionId: string): boolean =>
      state.insertionIdsWithPendingRegionalHighlight.includes(insertionId),
};

const clearRegionalHighlightSlots = (state: RegionalHighlightStateFields): void => {
  state.regionalHighlightSlotsResponse = undefined;
  state.regionalHighlightSlots = undefined;
};

export const regionalHighlightMutations = {
  setRegionalHighlightSlots(
    state: RegionalHighlightStateFields,
    slots: RegionalHighlightSlot[],
  ): void {
    state.regionalHighlightSlots = slots;
  },
  setRegionalHighlightSlotsResponse(
    state: RegionalHighlightStateFields,
    response: RegionalHighlightSlotsResponse,
  ): void {
    state.regionalHighlightSlotsResponse = response;
    state.regionalHighlightSlots = response.slots;
  },
  clearRegionalHighlightSlotsResponse(state: RegionalHighlightStateFields): void {
    clearRegionalHighlightSlots(state);
  },
  clearRegionalHighlightAgencyScopedData(state: RegionalHighlightStateFields): void {
    clearRegionalHighlightSlots(state);
    state.regionalHighlightPurchaseOptions = undefined;
    state.regionalHighlightPurchaseOrders = undefined;
    state.regionalHighlightLastPurchaseOrder = undefined;
  },
  setRegionalHighlightListingStatuses(
    state: RegionalHighlightStateFields,
    statuses: RegionalHighlightListingStatusesByListingId,
  ): void {
    state.regionalHighlightListingStatuses = statuses;
  },
  mergeRegionalHighlightListingStatuses(
    state: RegionalHighlightStateFields,
    statuses: RegionalHighlightListingStatusesByListingId,
  ): void {
    state.regionalHighlightListingStatuses = {
      ...(state.regionalHighlightListingStatuses ?? {}),
      ...statuses,
    };
  },
  setRegionalHighlightApiStatus(
    state: RegionalHighlightStateFields,
    payload: { operation: RegionalHighlightOperation; status: ApiCallStatus },
  ): void {
    state.regionalHighlightApiStatus = {
      ...state.regionalHighlightApiStatus,
      [payload.operation]: payload.status,
    };
  },
  setRegionalHighlightError(
    state: RegionalHighlightStateFields,
    payload: { operation: RegionalHighlightOperation; error: unknown },
  ): void {
    state.regionalHighlightErrors = {
      ...state.regionalHighlightErrors,
      [payload.operation]: payload.error,
    };
  },
  clearRegionalHighlightError(
    state: RegionalHighlightStateFields,
    operation: RegionalHighlightOperation,
  ): void {
    const { [operation]: _removed, ...remainingErrors } = state.regionalHighlightErrors;
    state.regionalHighlightErrors = remainingErrors;
  },
  setRegionalHighlightLastAssignment(
    state: RegionalHighlightStateFields,
    assignment: RegionalHighlightAssignmentResponse,
  ): void {
    state.regionalHighlightLastAssignment = assignment;
  },
  setRegionalHighlightLastCallback(
    state: RegionalHighlightStateFields,
    callback: RegionalHighlightCallbackResponse,
  ): void {
    state.regionalHighlightLastCallback = callback;
  },
  setRegionalHighlightPurchaseOptions(
    state: RegionalHighlightStateFields,
    options: RegionalHighlightPurchaseOptionsResponse,
  ): void {
    state.regionalHighlightPurchaseOptions = options;
  },
  setRegionalHighlightPurchaseOrders(
    state: RegionalHighlightStateFields,
    orders: RegionalHighlightPurchaseOrdersResponse,
  ): void {
    state.regionalHighlightPurchaseOrders = orders;
  },
  setRegionalHighlightLastPurchaseOrder(
    state: RegionalHighlightStateFields,
    order: RegionalHighlightPurchaseOrder,
  ): void {
    state.regionalHighlightLastPurchaseOrder = order;
  },
  appendToInsertionIdsWithPendingRegionalHighlight(
    state: RegionalHighlightStateFields,
    insertionId: string,
  ): void {
    if (state.insertionIdsWithPendingRegionalHighlight.includes(insertionId)) {
      return;
    }

    state.insertionIdsWithPendingRegionalHighlight = [
      ...state.insertionIdsWithPendingRegionalHighlight,
      insertionId,
    ];
  },
  removeFromInsertionIdsWithPendingRegionalHighlight(
    state: RegionalHighlightStateFields,
    insertionId: string,
  ): void {
    state.insertionIdsWithPendingRegionalHighlight =
      state.insertionIdsWithPendingRegionalHighlight.filter((id) => id !== insertionId);
  },
};

export const regionalHighlightActions = {
  async fetchRegionalHighlightSlots(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    listingId?: string,
  ): Promise<void> {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      context.commit('setRegionalHighlightApiStatus', {
        operation: 'slots',
        status: ApiCallStatus.ERROR,
      });
      return;
    }

    await runRegionalHighlightOperation(context, 'slots', activeAgencyId, async () => {
      const response = await ToplistingApiService.fetchRegionalHighlightSlots(
        activeAgencyId,
        listingId,
      );
      return () => context.commit('setRegionalHighlightSlotsResponse', response);
    });
  },
  async fetchRegionalHighlightSlotsForAddress(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    address: RegionalHighlightDraftAddress,
  ): Promise<void> {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId || !address.postalCode.trim()) {
      return;
    }

    context.commit('clearRegionalHighlightSlotsResponse');
    await runRegionalHighlightOperation(context, 'slots', activeAgencyId, async () => {
      const response = await ToplistingApiService.fetchRegionalHighlightSlotsForAddress(
        activeAgencyId,
        address,
      );
      return () => context.commit('setRegionalHighlightSlotsResponse', response);
    });
  },
  async recordRegionalHighlightPendingAssignment(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    payload: { propertyReferenceId: string; slotId: string },
  ): Promise<void> {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      throw new Error('Cannot record Regional Highlight intent without an active agency');
    }

    await ToplistingApiService.recordRegionalHighlightPendingAssignment(
      activeAgencyId,
      payload.propertyReferenceId,
      payload.slotId,
    );
  },
  async forgetRegionalHighlightPendingAssignment(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    propertyReferenceId: string,
  ): Promise<void> {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      return;
    }

    try {
      await ToplistingApiService.forgetRegionalHighlightPendingAssignment(
        activeAgencyId,
        propertyReferenceId,
      );
    } catch (error) {
      captureError(error);
    }
  },
  async fetchRegionalHighlightListingStatuses(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    listingIds: string[],
  ): Promise<void> {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId || listingIds.length === 0) {
      return;
    }

    await runRegionalHighlightOperation(context, 'listingStatuses', activeAgencyId, async () => {
      const statuses = await ToplistingApiService.fetchRegionalHighlightListingStatuses(
        activeAgencyId,
        { listingIds },
      );
      return () => context.commit('setRegionalHighlightListingStatuses', statuses);
    });
  },
  async assignRegionalHighlightSlot(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    payload: { slotId: string; assignment: RegionalHighlightAssignmentRequest },
  ): Promise<void> {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      return;
    }

    const assignmentSucceeded = await runRegionalHighlightOperation(
      context,
      'assign',
      activeAgencyId,
      async () => {
        const assignment = await ToplistingApiService.assignRegionalHighlightSlot(
          activeAgencyId,
          payload.slotId,
          payload.assignment,
        );
        return () => context.commit('setRegionalHighlightLastAssignment', assignment);
      },
    );
    if (assignmentSucceeded) {
      await refreshRegionalHighlightListingState(
        context,
        activeAgencyId,
        payload.assignment.listingId,
      );
    }
  },
  async deactivateRegionalHighlightSlot(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    payload: { slotId: string; deactivation?: RegionalHighlightDeactivationRequest },
  ): Promise<void> {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      return;
    }

    const deactivationSucceeded = await runRegionalHighlightOperation(
      context,
      'deactivate',
      activeAgencyId,
      async () => {
        await ToplistingApiService.deactivateRegionalHighlightSlot(
          activeAgencyId,
          payload.slotId,
          payload.deactivation,
        );
        return undefined;
      },
    );
    const listingId = payload.deactivation?.listingId;
    if (deactivationSucceeded && listingId) {
      context.commit(
        'setListingActionSuccess',
        'manageAdsPage.toasts.regionalHighlightWillBeDeactivated',
      );
      await refreshRegionalHighlightListingState(context, activeAgencyId, listingId);
    }
  },
  async requestRegionalHighlightCallback(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    request: RegionalHighlightCallbackRequest,
  ): Promise<void> {
    const activeAgencyId = useAgencyStore().activeAgencyId;
    if (!activeAgencyId) {
      return;
    }

    await runRegionalHighlightOperation(context, 'callback', activeAgencyId, async () => {
      const callback = await ToplistingApiService.requestRegionalHighlightCallback(
        activeAgencyId,
        request,
      );
      return () => context.commit('setRegionalHighlightLastCallback', callback);
    });
  },
  ...regionalHighlightPurchaseActions,
  /**
   * Follows a wizard publish until its deferred Regional Highlight booking exists.
   *
   * The booking is completed by `toplisting-service` only once the importer
   * `LISTING_PUBLISHED` event arrives, which is after the one-shot Manage Ads status
   * fetch, so without this the badge would need a manual reload. It lives in the
   * store to survive the navigation away from the wizard, and is meant to be
   * dispatched without awaiting.
   *
   * The pending marker is owned here rather than by the view, and cleared on every
   * exit path, so a pending badge cannot be stranded when the poll never starts.
   *
   * The backend exposes no pending or failed state, only `hasRegionalHighlight`, so
   * an exhausted budget is not proof of failure: the marker is dropped and the user
   * is nudged to refresh instead of being told the booking failed.
   */
  async trackRegionalHighlightAfterPublish(
    context: ActionContext<RegionalHighlightStateFields, InsertionFunnelState>,
    payload: { insertionId: string },
  ): Promise<void> {
    const { insertionId } = payload;

    if (context.state.insertionIdsWithPendingRegionalHighlight.includes(insertionId)) {
      return;
    }

    const activeAgencyId = useAgencyStore().activeAgencyId;
    const userId = resolveInsertionOwnerId(context, insertionId);

    if (!activeAgencyId || !userId) {
      return;
    }

    context.commit('appendToInsertionIdsWithPendingRegionalHighlight', insertionId);

    const confirmed = await runBackoffPoll(REGIONAL_HIGHLIGHT_POLL_DELAYS_MS, () =>
      hasRegionalHighlightLanded(context, { activeAgencyId, userId, insertionId }),
    );

    context.commit('removeFromInsertionIdsWithPendingRegionalHighlight', insertionId);

    if (!confirmed) {
      await showRegionalHighlightPendingTimeoutToast(context);
    }
  },
};
